/** Settings → Banking & Payments: three independent financial relationships, never interchangeable. */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, StatusPill, btnPrimary } from '../finance/financeUi';
import api from '../../lib/api';
import { PaymentAccountEnrollmentWizard } from './PaymentAccountEnrollmentWizard';

const Line = ({ label, value, testid }) => (
  <div className="flex justify-between border-b py-1.5 text-sm"><span className="text-muted-foreground">{label}</span><span data-testid={testid}>{value}</span></div>
);

function ReceivingBank({ rb }) {
  return (
    <Card title="Receiving Bank" testid="banking-receiving-card">
      <div className="space-y-2 px-5 py-4">
        <p className="text-xs text-muted-foreground">Where you receive residuals, commissions and payouts.</p>
        {rb?.bank_last4 ? <>
          <Line label="Bank" value={`•••• ${rb.bank_last4}`} testid="banking-receiving-last4" />
          <Line label="Status" value={<StatusPill value={rb.verification_status} testid="banking-receiving-status" />} />
          <Line label="Authorization" value="Payout credit" />
          <p className="text-xs text-muted-foreground">Bank changes are verified by MSC Finance before payouts use them.</p>
        </> : <p className="text-sm" data-testid="banking-receiving-empty">No receiving bank yet — add it during activation.</p>}
      </div>
    </Card>
  );
}

function PaymentAccountCard({ pa }) {
  const [open, setOpen] = useState(false);
  const acc = pa?.account;
  return (
    <Card title="Payment Account" testid="banking-payment-account-card"
          aside={acc ? <StatusPill value={acc.canonical_status} testid="banking-pa-status" /> : <StatusPill value="NOT_CONFIGURED" testid="banking-pa-status" />}>
      <div className="space-y-3 px-5 py-4">
        <p className="text-xs text-muted-foreground">Send ACH, pay your downline, debit customers or accept payments. Separate from your receiving bank and its authorization.</p>
        {pa?.program && <Line label="Provider" value={pa.program.provider_name} testid="banking-pa-provider" />}
        {acc || open ? <PaymentAccountEnrollmentWizard entry="settings" />
          : <button type="button" className={btnPrimary} onClick={() => setOpen(true)} disabled={!pa?.eligible || !pa?.program} data-testid="banking-setup-payments-btn">Set Up Payments</button>}
      </div>
    </Card>
  );
}

function BillingMethod({ bm }) {
  return (
    <Card title="ISOCNTRL Billing Method" testid="banking-billing-card">
      <div className="space-y-2 px-5 py-4">
        <p className="text-xs text-muted-foreground">Pays ISOCNTRL setup fees, subscriptions and platform charges.</p>
        {bm ? <>
          <Line label="Method" value={`ACH debit •••• ${bm.bank_last4 || ''}`} testid="banking-billing-method" />
          <Line label="Authorization" value="Subscription debit" />
        </> : <p className="text-sm" data-testid="banking-billing-empty">No billing method on file.</p>}
      </div>
    </Card>
  );
}

export default function BankingPaymentsTab() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.request('/payment-account/me/banking').then(setData).catch(() => setData({})), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="banking-payments-tab">
      <ReceivingBank rb={data.receiving_bank} />
      <PaymentAccountCard pa={data.payment_account} />
      <BillingMethod bm={data.billing_method} />
    </div>
  );
}
